import type { FC, MouseEvent } from 'react';
import { LinkOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { buildIncidentDetailPath } from '~/app/components/asset-detail/assetIncidentRoutes';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import { CurativeIncidentSummaryText } from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/CurativeIncidentSummaryText/CurativeIncidentSummaryText';
import {
  isCurativeTicketType,
  parseIncidentCodeFromTicketTitle,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { normalizeIncidentId } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils/curative-incident-fields';

export type CurativeIncidentActionsProps = {
  ticketType: string;
  ticketTitle: string;
  incidentId: string | null;
  incidentCode: string | null;
  incidentTitle: string | null;
  onBeforeNavigate?: () => void;
  /** Kanban chip: truncate long summaries. Drawer: omit for more lines. */
  summaryMaxRows?: number;
};

export const CurativeIncidentActions: FC<CurativeIncidentActionsProps> = ({
  ticketType,
  ticketTitle,
  incidentId,
  incidentCode,
  incidentTitle,
  onBeforeNavigate,
  summaryMaxRows = 2,
}) => {
  const { t } = useTranslation('maintenance');
  const navigate = useNavigate();

  if (!isCurativeTicketType(ticketType)) {
    return null;
  }

  const linkedIncidentId = normalizeIncidentId(incidentId) ?? '';
  const displayCode =
    incidentCode?.trim() ?? parseIncidentCodeFromTicketTitle(ticketTitle);
  const fallbackTitle = incidentTitle?.trim() ?? null;

  const openIncidentPage = (event: MouseEvent) => {
    event.stopPropagation();
    if (!linkedIncidentId) return;
    onBeforeNavigate?.();
    navigate(buildIncidentDetailPath(linkedIncidentId));
  };

  return (
    <div style={{ marginTop: 6 }} onClick={(event) => event.stopPropagation()}>
      <Typography.Text
        strong
        style={{
          display: 'block',
          fontSize: 12,
          color: WORK_ORDER_UI.sidebarNavy,
        }}
      >
        {t('workOrders.kanban.correctiveMaintenanceLabel')}
      </Typography.Text>

      {displayCode ? (
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginTop: 2, fontSize: 11 }}
        >
          {t('workOrders.kanban.incidentCodeLabel', { code: displayCode })}
        </Typography.Text>
      ) : null}

      {linkedIncidentId ? (
        <>
          <CurativeIncidentSummaryText
            fallbackTitle={fallbackTitle}
            maxRows={summaryMaxRows}
          />
          <Typography.Link
            onClick={openIncidentPage}
            style={{ fontSize: 11, marginTop: 4, display: 'inline-block' }}
          >
            <LinkOutlined /> {t('workOrders.kanban.detail.openIncidentPage')}
          </Typography.Link>
        </>
      ) : null}
    </div>
  );
};
